import type { ReactNode } from 'react';

import { SOFT_TONE_CLASS } from '../styles/tone.js';

export type BadgeVariant = 'default' | 'danger' | 'info' | 'success' | 'warning';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  default: 'border-accent bg-brand text-textOnBrand',
  // Deliberately NOT the shared soft `danger` treatment `StatusMessage`/
  // `ErrorState` use: this badge is a notification *count*, conventionally
  // read as a solid, high-urgency fill rather than a softer inline tone —
  // see `styles/tone.ts`'s doc comment for why this one tone stays local.
  danger: 'border-danger bg-danger text-white',
  info: SOFT_TONE_CLASS.info,
  success: SOFT_TONE_CLASS.success,
  warning: SOFT_TONE_CLASS.warning,
};

/** A small numeric/status indicator (TECH_STACK §16) — used for the unread-notification count. */
export function Badge({ children, variant = 'default' }: BadgeProps) {
  return (
    <span
      className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full border px-1.5 py-0.5 text-xs font-semibold leading-none ${VARIANT_CLASSES[variant]}`}
    >
      {children}
    </span>
  );
}
